import { buildHeader, buildParagraph, buildNewlines, createTableHeaderCell, createTableCell } from "./helpers";
import { getStandardsMetadata, getMasteryByStandard } from "@/services/query/skillsMasteryAPI";
import { Table, TableRow } from "docx";
import store from "@/store";
import { selectUserEntitlement } from "@/store/slices";
import { getSchoolYearDates } from "./utils/schoolYearUtils";
import { getMasteryStatus } from "../../utilities/masteryUtils";
import { formatGradeDisplay, formatPercentage } from "./utils/formatUtils";



const createStandardsTable = (standardsData) => {
  const headers = ["Standard ID", "Description", "Mastery Level"];
  const headerRow = new TableRow({
    children: headers.map(header => createTableHeaderCell(header))
  });
  
  const dataRows = standardsData.map(standard => 
    new TableRow({
      children: [
        createTableCell({ text: standard.standardId || "N/A" }),
        createTableCell({ text: standard.description || "No description available" }),
        createTableCell({ 
          text: (standard.averageMastery !== undefined && standard.averageMastery !== null)
            ? `${formatPercentage(standard.averageMastery)} (${getMasteryStatus(standard.averageMastery)})` 
            : "No data"
        })
      ]
    })
  );

  return new Table({
    rows: [headerRow, ...dataRows],
    width: {
      size: 100,
      type: "pct"
    }
  });
};

const createSectionStandards = async (studentData, weeklyPlan) => {
  try {
    // Default to grade 2 if no grade specified
    const grade = studentData.grade || "2";
    
    // Get standardset from Redux store (same as Standards Mastery Report)
    const state = store.getState();
    const userEntitlement = selectUserEntitlement(state);
    const standardSet = userEntitlement.data.sorConfig.selectedStandard || "COMMON_CORE"; // Default to COMMON_CORE if not set
    

    
    // Fetch standards metadata for student's grade
    const standardsMetadata = await getStandardsMetadata(standardSet, grade);
    
    if (!standardsMetadata || standardsMetadata.length === 0) {
      return [
        buildHeader(`${studentData.firstName}'s Mastery of ${formatGradeDisplay(studentData.grade)} Reading Standards`, 3),
        buildParagraph("No standards data available for this grade level.", { italics: true }),
        ...buildNewlines(4),
      ];
    }

    // Calculate date range for school year using calendaring service
    const currentDate = new Date();
    const { startDate: yearStartDate } = await getSchoolYearDates();
    
    // Get mastery data for this student
    const standardIds = standardsMetadata.map(s => s.standardId);
    const masteryData = await getMasteryByStandard(
      standardIds, 
      [studentData.id], 
      yearStartDate.toISOString().split("T")[0],
      currentDate.toISOString().split("T")[0]
    );
    
    // Combine metadata with mastery data
    const combinedData = standardsMetadata.map(standard => {
      const mastery = masteryData.find(m => m.standardId === standard.standardId);
      return {
        ...standard,
        averageMastery: mastery?.averageMastery
      };
    });

    return [
      buildHeader(`${studentData.firstName}'s Mastery of ${formatGradeDisplay(studentData.grade)} Reading Standards`, 3),
      ...buildNewlines(1),
      createStandardsTable(combinedData),
      ...buildNewlines(4),
    ];
    
  } catch (error) {
    console.error("Error fetching standards data:", error);
    return [
      buildHeader(`${studentData.firstName}'s Mastery of ${formatGradeDisplay(studentData.grade)} Reading Standards`, 3),
      buildParagraph("Unable to load standards data at this time.", { italics: true }),
      ...buildNewlines(4),
    ];
  }
};

export default createSectionStandards;
